import { useEffect, useMemo, useState } from 'react';

import AutoScalingStateFilter from '~/shared/components/filters/AutoScalingStateFilter';
import FiltersDisclosure from '~/shared/components/filters/FiltersDisclosure';
import SearchInstance from '~/shared/components/filters/search/SearchInstance';
import SearchVolumeId from '~/shared/components/filters/search/SearchVolumeId';
import SearchVolumeTags from '~/shared/components/filters/search/SearchVolumeTags';
import SearchClusterName from '~/shared/components/filters/search/SearchClusterName';
import SearchAccount from '~/shared/components/filters/search/SearchAccount';
import { SelectOption, Account } from '~/shared/utils/types';

interface FilterPanelContentProps {
  panelOpen?: boolean;
  pendingAutoscaleState: string[];
  setPendingAutoscaleState: (state: string[]) => void;
  pendingClusterNameFilters: SelectOption[];
  setPendingClusterNameFilters: (filters: SelectOption[]) => void;
  pendingInstanceFilters: SelectOption[];
  setPendingInstanceFilters: (filters: SelectOption[]) => void;
  pendingVolumeIdFilters: SelectOption[];
  setPendingVolumeIdFilters: (filters: SelectOption[]) => void;
  pendingVolumeTagFilters: SelectOption[];
  setPendingVolumeTagFilters: (filters: SelectOption[]) => void;
  pendingAccountFilters: SelectOption[];
  setPendingAccountFilters: (filters: SelectOption[]) => void;
  isOrgView?: boolean;
  accounts?: Account[];
}

export default function FilterPanelContent({
  panelOpen = false,
  pendingAutoscaleState,
  setPendingAutoscaleState,
  pendingClusterNameFilters,
  setPendingClusterNameFilters,
  pendingInstanceFilters,
  setPendingInstanceFilters,
  pendingVolumeIdFilters,
  setPendingVolumeIdFilters,
  pendingVolumeTagFilters,
  setPendingVolumeTagFilters,
  pendingAccountFilters,
  setPendingAccountFilters,
  isOrgView = false,
  accounts = [],
}: FilterPanelContentProps) {
  const [isAccountDisclosureOpen, setIsAccountDisclosureOpen] = useState(false);
  const [isClusterNameDisclosureOpen, setIsClusterNameDisclosureOpen] = useState(false);
  const [isInstanceDisclosureOpen, setIsInstanceDisclosureOpen] = useState(false);
  const [isVolumeIdDisclosureOpen, setIsVolumeIdDisclosureOpen] = useState(false);
  const [isVolumeTagDisclosureOpen, setIsVolumeTagDisclosureOpen] = useState(false);

  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (!panelOpen) {
      setHasInitialized(false);
      return;
    }

    if (hasInitialized) {
      return;
    }

    // Check which disclosures should be open based on filters
    const shouldOpenAccount = pendingAccountFilters.length > 0;
    const shouldOpenClusterName = pendingClusterNameFilters.length > 0;
    const shouldOpenInstance = pendingInstanceFilters.length > 0;
    const shouldOpenVolumeId = pendingVolumeIdFilters.length > 0;
    const shouldOpenVolumeTags = pendingVolumeTagFilters.length > 0;

    const hasAnyOpen =
      isAccountDisclosureOpen ||
      isClusterNameDisclosureOpen ||
      isInstanceDisclosureOpen ||
      isVolumeIdDisclosureOpen ||
      isVolumeTagDisclosureOpen ||
      shouldOpenAccount ||
      shouldOpenClusterName ||
      shouldOpenInstance ||
      shouldOpenVolumeId ||
      shouldOpenVolumeTags;

    if (shouldOpenAccount) {
      setIsAccountDisclosureOpen(true);
    }
    if (shouldOpenClusterName) {
      setIsClusterNameDisclosureOpen(true);
    }
    if (shouldOpenInstance) {
      setIsInstanceDisclosureOpen(true);
    }
    if (shouldOpenVolumeId) {
      setIsVolumeIdDisclosureOpen(true);
    }
    if (shouldOpenVolumeTags) {
      setIsVolumeTagDisclosureOpen(true);
    }

    // No default open disclosure - user must manually open the ones they want

    setHasInitialized(true);
  }, [
    panelOpen,
    hasInitialized,
    pendingAccountFilters.length,
    pendingClusterNameFilters.length,
    pendingInstanceFilters.length,
    pendingVolumeIdFilters.length,
    pendingVolumeTagFilters.length,
  ]);

  const accountsScope = useMemo(() => {
    if (!isOrgView) return undefined;
    return pendingAccountFilters.length > 0
      ? pendingAccountFilters.map((f) => f.id).join(',')
      : 'all';
  }, [isOrgView, pendingAccountFilters]);

  const handleClearClusterNameFilters = () => setPendingClusterNameFilters([]);
  const handleClearInstanceFilters = () => setPendingInstanceFilters([]);
  const handleClearVolumeIdFilters = () => setPendingVolumeIdFilters([]);
  const handleClearVolumeTagFilters = () => setPendingVolumeTagFilters([]);

  const handleClearAccountFilters = () => {
    setPendingAccountFilters([]);
  };

  return (
    <div className="flex-1 overflow-y-auto pb-4">
      <AutoScalingStateFilter
        pendingAutoscaleState={pendingAutoscaleState}
        setPendingAutoscaleState={setPendingAutoscaleState}
      />
      {isOrgView && accounts.length > 0 && (
        <FiltersDisclosure
          title="Accounts"
          count={pendingAccountFilters.length > 0 ? pendingAccountFilters.length : undefined}
          className="px-8 border-b border-gray-300"
          open={isAccountDisclosureOpen}
          onOpenChange={setIsAccountDisclosureOpen}
          onClearFilters={handleClearAccountFilters}
        >
          <SearchAccount
            filters={pendingAccountFilters}
            setFilters={(newSelections) =>
              setPendingAccountFilters(newSelections.filter((f) => f.group === 'accountId'))
            }
            accounts={accounts}
            isDisclosureOpen={isAccountDisclosureOpen}
          />
        </FiltersDisclosure>
      )}
      <FiltersDisclosure
        title="Cluster Name"
        count={pendingClusterNameFilters?.length > 0 ? pendingClusterNameFilters.length : undefined}
        className="px-8 border-b border-gray-300"
        open={isClusterNameDisclosureOpen}
        onOpenChange={setIsClusterNameDisclosureOpen}
        onClearFilters={handleClearClusterNameFilters}
      >
        <SearchClusterName
          filters={pendingClusterNameFilters}
          setFilters={(newSelections) => {
            setPendingClusterNameFilters(newSelections.filter((f) => f.group === 'clusterName'));
          }}
          isDisclosureOpen={isClusterNameDisclosureOpen}
          accounts={accountsScope}
        />
      </FiltersDisclosure>

      <FiltersDisclosure
        title="Instance ID"
        count={pendingInstanceFilters.length > 0 ? pendingInstanceFilters.length : undefined}
        className="px-8 border-b border-gray-300"
        open={isInstanceDisclosureOpen}
        onOpenChange={setIsInstanceDisclosureOpen}
        onClearFilters={handleClearInstanceFilters}
      >
        <SearchInstance
          filters={pendingInstanceFilters}
          setFilters={(newSelections) => {
            setPendingInstanceFilters(newSelections.filter((f) => f.group === 'instanceId'));
          }}
          isDisclosureOpen={isInstanceDisclosureOpen}
          accounts={accountsScope}
        />
      </FiltersDisclosure>
      <FiltersDisclosure
        title="Volume ID"
        count={pendingVolumeIdFilters.length > 0 ? pendingVolumeIdFilters.length : undefined}
        className="px-8 border-b border-gray-300 "
        open={isVolumeIdDisclosureOpen}
        onOpenChange={setIsVolumeIdDisclosureOpen}
        onClearFilters={handleClearVolumeIdFilters}
      >
        <SearchVolumeId
          filters={pendingVolumeIdFilters}
          setFilters={(newSelections) =>
            setPendingVolumeIdFilters(newSelections.filter((f) => f.group === 'volumeId'))
          }
          isDisclosureOpen={isVolumeIdDisclosureOpen}
          accounts={accountsScope}
        />
      </FiltersDisclosure>
      <FiltersDisclosure
        title="Volume Tags"
        count={pendingVolumeTagFilters.length > 0 ? pendingVolumeTagFilters.length : undefined}
        className="px-8 border-b border-gray-300"
        open={isVolumeTagDisclosureOpen}
        onOpenChange={setIsVolumeTagDisclosureOpen}
        onClearFilters={handleClearVolumeTagFilters}
      >
        <SearchVolumeTags
          filters={pendingVolumeTagFilters}
          setFilters={(newSelections) =>
            setPendingVolumeTagFilters(newSelections.filter((f) => f.group.startsWith('tag:')))
          }
          isDisclosureOpen={isVolumeTagDisclosureOpen}
          accounts={accountsScope}
        />
      </FiltersDisclosure>
    </div>
  );
}
